import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fileUrl, inr, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { FileUploadField, ListEditor, PickupEditor } from "@/components/admin/AdminFields";
import { Plus, Pencil, Trash2, X, Eye, Star } from "lucide-react";

const SPECIAL_TAGS = ["Trip with Founder", "Trip with Creator", "Exclusive Edition", "Limited Batch", "Women Only", "Photography Special"];

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const EMPTY = {
  name: "", slug: "", destination: "", category: "", duration: "", starting_price: 0, prices: { quad: "", triple: "", double: "" },
  cover_image: null, gallery: [], overview: "", highlights: [], itinerary: [],
  inclusions: [], exclusions: [], important_info: [], pickup_points: [], pickups: [], faqs: [],
  pdf_url: null, active: true, featured: false, special: false, special_tag: "", special_note: "", festive: false, festive_tag: "", festive_note: "", festive_ends_on: "", seo_title: "", seo_description: "", creator: false, creator_name: "", creator_handle: "", creator_photo: null, creator_bio: "", creator_followers: "", creator_photos: [], creator_collab: "", creator_links: [],
};

export default function TripsManage() {
  const [trips, setTrips] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/trips").then(({ data }) => setTrips(data));
  useEffect(() => { load(); api.get("/categories").then(({ data }) => setCats(data)); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t }); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.destination || !form.category) { toast.error("Name, destination and category are required"); return; }
    setSaving(true);
    const prices = { quad: Number(form.prices?.quad), triple: Number(form.prices?.triple), double: Number(form.prices?.double) };
    if (!(prices.quad > 0 && prices.triple > 0 && prices.double > 0)) { toast.error("Quad, Triple and Double prices are all required"); setSaving(false); return; }
    const payload = { ...form, slug: form.slug || slugify(form.name), prices, starting_price: prices.quad };
    try {
      if (editing) await api.put(`/admin/trips/${editing.id}`, payload);
      else await api.post("/admin/trips", payload);
      toast.success(editing ? "Trip updated" : "Trip created");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (t) => {
    await api.put(`/admin/trips/${t.id}`, { ...EMPTY, ...t, active: !t.active });
    load();
  };

  const del = async (t) => { await api.delete(`/admin/trips/${t.id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-4xl font-semibold">Trips</h1><p className="text-muted-foreground">{trips.length} trips</p></div>
        <Button className="rounded-full" onClick={openNew} data-testid="add-trip"><Plus className="h-4 w-4 mr-1" /> Add Trip</Button>
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left"><tr>
              <th className="p-3">Trip</th><th className="p-3">Category</th><th className="p-3">Price</th>
              <th className="p-3">Departures</th><th className="p-3">Active</th><th className="p-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {trips.map((t) => (
                <tr key={t.id} className="border-t hover:bg-muted/30" data-testid={`trip-row-${t.slug}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <img src={fileUrl(t.cover_image)} alt="" className="h-10 w-14 rounded object-cover" />
                      <div><p className="font-medium flex items-center gap-1">{t.name} {t.featured && <Star className="h-3.5 w-3.5 text-primary fill-primary" />}</p><p className="text-xs text-muted-foreground">{t.destination}</p></div>
                    </div>
                  </td>
                  <td className="p-3 capitalize">{t.category?.replace("-", " ")}</td>
                  <td className="p-3 font-mono text-xs">{t.prices ? <>Q {inr(t.prices.quad)}<br />T {inr(t.prices.triple)}<br />D {inr(t.prices.double)}</> : inr(t.starting_price)}</td>
                  <td className="p-3">{t.departure_count || 0}</td>
                  <td className="p-3"><Switch checked={t.active} onCheckedChange={() => toggleActive(t)} data-testid={`toggle-${t.slug}`} /></td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <a href={`/trips/${t.slug}`} target="_blank" rel="noreferrer"><Button variant="ghost" size="icon"><Eye className="h-4 w-4" /></Button></a>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-${t.slug}`}><Pencil className="h-4 w-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive" data-testid={`delete-${t.slug}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete {t.name}?</AlertDialogTitle><AlertDialogDescription>This also removes its departures. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(t)} className="bg-destructive">Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="trip-form">
          <DialogHeader><DialogTitle className="font-heading text-3xl">{editing ? "Edit Trip" : "Add Trip"}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="trip-name" /></div>
              <div><Label>Slug (URL)</Label><Input value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} placeholder={slugify(form.name)} /></div>
              <div><Label>Destination *</Label><Input value={form.destination} onChange={(e) => set("destination", e.target.value)} data-testid="trip-destination" /></div>
              <div>
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="trip-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Duration</Label><Input value={form.duration} onChange={(e) => set("duration", e.target.value)} placeholder="5 Days / 4 Nights" /></div>
              <div className="sm:col-span-2 rounded-xl border p-3" data-testid="trip-prices">
                <Label>Price per person by room sharing (₹) *</Label>
                <div className="grid grid-cols-3 gap-3 mt-1">
                  {[["quad", "Quad"], ["triple", "Triple"], ["double", "Double"]].map(([k, l]) => (
                    <div key={k}><Label className="text-xs text-muted-foreground">{l} sharing</Label><Input type="number" min="1" value={form.prices?.[k] ?? ""} onChange={(e) => set("prices", { ...form.prices, [k]: e.target.value })} data-testid={`trip-price-${k}`} /></div>
                  ))}
                </div>
              </div>
            </div>

            <div><Label>Overview</Label><Textarea value={form.overview} onChange={(e) => set("overview", e.target.value)} rows={3} /></div>

            <FileUploadField label="Cover Image" value={form.cover_image} onChange={(v) => set("cover_image", v)} />

            <div>
              <Label>Gallery Images</Label>
              <div className="flex flex-wrap gap-2 mt-1 mb-2">
                {form.gallery.map((g, i) => (
                  <div key={i} className="relative"><img src={fileUrl(g)} alt="" className="h-16 w-20 rounded object-cover border" />
                    <button type="button" onClick={() => set("gallery", form.gallery.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
              <FileUploadField value={null} preview={false} onChange={(v) => v && set("gallery", [...form.gallery, v])} />
            </div>

            <FileUploadField label="Itinerary PDF" value={form.pdf_url} onChange={(v) => set("pdf_url", v)} accept="application/pdf" />

            <ListEditor label="Highlights" items={form.highlights} onChange={(v) => set("highlights", v)} placeholder="Add a highlight" />

            <div>
              <Label>Day-wise Itinerary</Label>
              <div className="space-y-2 mt-1">
                {form.itinerary.map((it, i) => (
                  <div key={i} className="grid sm:grid-cols-[60px_1fr_auto] gap-2 items-start rounded-lg border p-2">
                    <Input type="number" value={it.day} onChange={(e) => set("itinerary", form.itinerary.map((x, j) => j === i ? { ...x, day: Number(e.target.value) } : x))} placeholder="Day" />
                    <div className="space-y-1">
                      <Input value={it.title} onChange={(e) => set("itinerary", form.itinerary.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} placeholder="Title" />
                      <Textarea value={it.description} onChange={(e) => set("itinerary", form.itinerary.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} placeholder="Description" rows={2} />
                    </div>
                    <Button type="button" variant="ghost" size="icon" className="text-destructive" onClick={() => set("itinerary", form.itinerary.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => set("itinerary", [...form.itinerary, { day: form.itinerary.length + 1, title: "", description: "" }])}><Plus className="h-4 w-4 mr-1" /> Add Day</Button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <ListEditor label="Inclusions" items={form.inclusions} onChange={(v) => set("inclusions", v)} />
              <ListEditor label="Exclusions" items={form.exclusions} onChange={(v) => set("exclusions", v)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <ListEditor label="Important Info" items={form.important_info} onChange={(v) => set("important_info", v)} />
              <PickupEditor items={form.pickups?.length ? form.pickups : (form.pickup_points || []).map((n) => ({ name: n, extra: 0 }))} onChange={(v) => { set("pickups", v); set("pickup_points", v.map((x) => x.name)); }} />
            </div>

            <div>
              <Label>FAQs</Label>
              <div className="space-y-2 mt-1">
                {form.faqs.map((f, i) => (
                  <div key={i} className="rounded-lg border p-2 space-y-1">
                    <div className="flex gap-2">
                      <Input value={f.q} onChange={(e) => set("faqs", form.faqs.map((x, j) => j === i ? { ...x, q: e.target.value } : x))} placeholder="Question" />
                      <Button type="button" variant="ghost" size="icon" className="text-destructive" onClick={() => set("faqs", form.faqs.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
                    </div>
                    <Textarea value={f.a} onChange={(e) => set("faqs", form.faqs.map((x, j) => j === i ? { ...x, a: e.target.value } : x))} placeholder="Answer" rows={2} />
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => set("faqs", [...form.faqs, { q: "", a: "" }])}><Plus className="h-4 w-4 mr-1" /> Add FAQ</Button>
              </div>
            </div>

            <div className="rounded-xl border border-gold/50 bg-gold/5 p-4 space-y-3" data-testid="special-section">
              <label className="flex items-center gap-2 font-medium"><Switch checked={form.special} onCheckedChange={(v) => set("special", v)} data-testid="trip-special" /> Safarshala Special (show in the exclusive section on Home)</label>
              {form.special && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Special tag</Label>
                    <Select value={form.special_tag || ""} onValueChange={(v) => set("special_tag", v)}>
                      <SelectTrigger data-testid="trip-special-tag"><SelectValue placeholder="Choose tag" /></SelectTrigger>
                      <SelectContent>{SPECIAL_TAGS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Host / note</Label><Input value={form.special_note || ""} onChange={(e) => set("special_note", e.target.value)} placeholder="e.g. Hosted by @travelwithriya" data-testid="trip-special-note" /></div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-amber-400/50 bg-amber-500/5 p-4 space-y-3" data-testid="festive-section">
              <label className="flex items-center gap-2 font-medium"><Switch checked={form.festive} onCheckedChange={(v) => set("festive", v)} data-testid="trip-festive" /> Festive Special (show in the Festive section on Home)</label>
              {form.festive && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Festive tag</Label><Input value={form.festive_tag || ""} onChange={(e) => set("festive_tag", e.target.value)} placeholder="e.g. Diwali Getaway, Holi Special" data-testid="trip-festive-tag" /></div>
                  <div><Label>Offer note</Label><Input value={form.festive_note || ""} onChange={(e) => set("festive_note", e.target.value)} placeholder="e.g. Flat ₹1000 off with code DIWALI" data-testid="trip-festive-note" /></div>
                  <div><Label>Offer ends on <span className="text-muted-foreground">(shows countdown; card hides after this date)</span></Label><Input type="date" value={form.festive_ends_on || ""} onChange={(e) => set("festive_ends_on", e.target.value)} data-testid="trip-festive-ends" /></div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-fuchsia-400/50 bg-fuchsia-500/5 p-4 space-y-3" data-testid="creator-section">
              <label className="flex items-center gap-2 font-medium"><Switch checked={!!form.creator} onCheckedChange={(v) => set("creator", v)} data-testid="trip-creator" /> Creator Trip (hosted by an Instagram creator — shows in the Creator section on Home)</label>
              {form.creator && (
                <div className="space-y-3">
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div><Label>Creator name *</Label><Input value={form.creator_name || ""} onChange={(e) => set("creator_name", e.target.value)} placeholder="Riya Sharma" data-testid="trip-creator-name" /></div>
                    <div><Label>Instagram handle</Label><Input value={form.creator_handle || ""} onChange={(e) => set("creator_handle", e.target.value.replace(/^@/, ""))} placeholder="travelwithriya" data-testid="trip-creator-handle" /></div>
                    <div><Label>Followers (display text)</Label><Input value={form.creator_followers || ""} onChange={(e) => set("creator_followers", e.target.value)} placeholder="120K" data-testid="trip-creator-followers" /></div>
                  </div>
                  <FileUploadField label="Creator profile photo" value={form.creator_photo} onChange={(v) => set("creator_photo", v)} />
                  <div><Label>About the creator</Label><Textarea rows={2} value={form.creator_bio || ""} onChange={(e) => set("creator_bio", e.target.value)} placeholder="Mountain lover, 40+ treks, known for…" data-testid="trip-creator-bio" /></div>
                  <div><Label>Collaboration details (what travellers get: meetups, content shoots, Q&A, giveaways…)</Label><Textarea rows={3} value={form.creator_collab || ""} onChange={(e) => set("creator_collab", e.target.value)} data-testid="trip-creator-collab" /></div>
                  <ListEditor label="Other links (YouTube, website…)" items={form.creator_links || []} onChange={(v) => set("creator_links", v)} placeholder="https://youtube.com/@…" />
                  <div>
                    <Label>Creator / past-trip photos</Label>
                    <div className="flex flex-wrap gap-2 mt-1 mb-2">
                      {(form.creator_photos || []).map((g, i) => (
                        <div key={i} className="relative"><img src={fileUrl(g)} alt="" className="h-16 w-20 rounded object-cover border" />
                          <button type="button" onClick={() => set("creator_photos", form.creator_photos.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
                        </div>
                      ))}
                    </div>
                    <FileUploadField value={null} preview={false} onChange={(v) => v && set("creator_photos", [...(form.creator_photos || []), v])} />
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border p-4 space-y-3" data-testid="trip-seo-section">
              <p className="font-medium">Google search preview (SEO)</p>
              <div className="grid gap-3">
                <div><Label>Search title <span className="text-muted-foreground">({(form.seo_title || "").length}/60)</span></Label><Input maxLength={70} value={form.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} placeholder={`${form.name || "Trip name"} — ${form.duration || "duration"}`} data-testid="trip-seo-title" /></div>
                <div><Label>Search description <span className="text-muted-foreground">({(form.seo_description || "").length}/160)</span></Label><Textarea rows={2} maxLength={170} value={form.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} placeholder="What travellers will read under your link on Google…" data-testid="trip-seo-desc" /></div>
              </div>
              <div className="rounded-lg bg-muted/50 p-3 text-sm">
                <p className="text-[#1a0dab] dark:text-blue-400 text-base truncate">{form.seo_title || `${form.name || "Trip name"} — ${form.duration || ""}`} | Safarshala</p>
                <p className="text-emerald-700 dark:text-emerald-400 text-xs">/trips/{form.slug || slugify(form.name || "")}</p>
                <p className="text-muted-foreground line-clamp-2">{form.seo_description || form.overview || "Description preview…"}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <label className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} /> Active</label>
              <label className="flex items-center gap-2"><Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} /> Featured</label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="save-trip">{saving ? "Saving…" : "Save Trip"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
